<template>
  <div class="ai-controller" :class="{ 'ai-controller--gpt-enabled': isAhaSlidesGPTEnabled }">
    <div class="overlay-disable-edit" v-if="isDisableEditPresentation" />
    <Conversation
      :conversation="conversation"
      :systemError="systemError"
      :promptSuggestions="promptSuggestions"
      :lastUserMessageIndex="lastUserMessageIndex"
      @clickOnAIRetryButton="clickOnAIRetryButton"
      @clickOnSuggestion="clickOnSuggestion"
    />
    <div id="ai-conversation-footer" class="px-4">
      <AIPromptInput
        :isSendingAIRequest="isSendingAIRequest"
        :isDoneGenerating="isDoneGenerating"
        :isDocToSlideGenerating="isDocToSlideGenerating"
        :isAhaSlidesGPTEnabled="isAhaSlidesGPTEnabled"
        @clickOnStopGeneratingSlidesButton="clickOnStopGeneratingSlidesButton"
        @clickOnGenerateSlidesButton="clickOnGenerateSlidesButton"
        @enterAIPromptInput="enterAIPromptInput"
        @clickOnAIPromptInputAttachmentButton="clickOnAIPromptInputAttachmentButton"
      />
      <AhaSlidesGPTTouchPoint v-if="isAhaSlidesGPTEnabled" :ahaGPTUrl="ahaGPTUrl" />
    </div>
    <AIGenerateSlidesResultModal
      v-if="isAIResultModalVisible"
      :isSendingAIRequest="isSendingAIRequest"
      :isDoneGenerating="isDoneGenerating"
      :isSendingAIRequestFeedback="isSendingAIRequestFeedback"
      :hasStoppedGenerating="hasStoppedGenerating"
      :loadingProgressBar="loadingProgressBar"
      @clickOnAddSlideButton="clickOnAddSlideButton"
      @clickOnFeedbackButton="clickOnFeedbackButton"
      v-aha-emit-action.view
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import * as Sentry from '@sentry/vue'
import CanvasBlocksUpdateMixin from '@/mixins/presentation/CanvasBlocksUpdateMixin'
import UndoRedoMixin from '@/mixins/undo-redo/UndoRedoMixin'
import OpenAIConstants from '@/constant/openAI'
import utils from '@/util'
import eventCategories from '@/constant/eventCategories'

import Conversation from '@/components-v2/presentation-editor/side-right/right-nav/Conversation.vue'
import AIPromptInput from '@/components-v2/presentation-editor/side-right/right-nav/AIPromptInput.vue'
import AhaSlidesGPTTouchPoint from '@/components-v2/presentation-editor/side-right/right-nav/AhaSlidesGPTTouchPoint.vue'
import AIGenerateSlidesResultModal from '@/components-v2/presentation-editor/side-right/right-nav/AIGenerateSlidesResultModal.vue'

export default {
  components: {
    Conversation,
    AIPromptInput,
    AhaSlidesGPTTouchPoint,
    AIGenerateSlidesResultModal,
  },
  mixins: [CanvasBlocksUpdateMixin, UndoRedoMixin],
  computed: {
    ...mapGetters('promptToSlideGenerator', [
      'aiResponse',
      'isSendingAIRequest',
      'isSendingAIRequestFeedback',
      'isDoneGenerating',
      'hasStoppedGenerating',
      'systemError',
      'isAIResultModalVisible',
      'presentationRequestId',
    ]),
    ...mapGetters('aiConversation', ['conversation', 'lastUserMessageIndex', 'isMessageAddedFromImportPpt']),
    ...mapGetters('progressBar', ['loadingProgressBar']),
    ...mapGetters('aiPromptSuggestion', ['promptSuggestions']),
    ...mapGetters('docToSlideGenerator', [
      'docToSlideResponseError',
      'isDocToSlideGenerating',
      'docToSlideUniqueRequestId',
    ]),
    ...mapGetters('ahaGpt', ['isAhaSlidesGPTEnabled', 'ahaGPTUrl']),

    ...mapState({
      presentation: (state) => state.presentation,
      currentUser: (state) => state.currentUser,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
    }),
  },
  watch: {
    async isSendingAIRequest() {
      if (!this.isSendingAIRequest) return
      try {
        await utils.sleep(500)
        this.createNewMessageAction({
          presentationId: this.presentation.id,
          role: OpenAIConstants.AI_ROLE_SYSTEM,
          content: this.$i18nPresenter.t(OpenAIConstants.aiConversationPendingMessages[0]),
          type: OpenAIConstants.AI_SYSTEM_PENDING_MESSAGE_TYPE,
        })
        this.changePendingMessageContentOnIntervalsAction()
      } catch (error) {
        Sentry.captureException(error)
      }
    },
  },
  async mounted() {
    if (!this.isMessageAddedFromImportPpt) { // If the message is not added from global import ppt, reset the conversation
      this.showGreetingsAndSuggestion()
      return
    }
    await utils.sleep(500)
    this.setAddMessageSourceAction('') // Reset the source of the message added from global import ppt
  },
  methods: {
    ...mapActions('rightNav', ['setRightNavActiveTabAction']),
    ...mapActions('aiConversation', [
      'createNewMessageAction',
      'resetNumberOfSlidesAddedAction',
      'setGreetingsMessageAction',
      'setAddMessageSourceAction',
      'changePendingMessageContentOnIntervalsAction',
    ]),
    ...mapActions('promptToSlideGenerator', ['generateSlidesAction',
      'retryAISlidesGeneratorAction',
      'generateSlidesFromFeedbackAction',
      'stopGeneratingSlidesAction',
      'setUserPromptAction',
    ]),
    ...mapActions('aiPromptSuggestion', ['getPromptSuggestionsAction']),
    ...mapActions('slideInsertionToPresentation', ['addSlideToPresentationAction']),
    ...mapActions('docToSlideGenerator', ['toggleDocToSlideImportModalAction',
      'setDocToSlideResponseErrorAction',
    ]),

    clickOnAIRetryButton(mode) {
      this.retryAISlidesGeneratorAction(mode)
    },

    clickOnSuggestion(suggestion) {
      try {
        if (!suggestion || !suggestion.length) return
        this.setUserPromptAction(suggestion)
        this.resetNumberOfSlidesAddedAction()
        this.createNewMessageAction({
          presentationId: this.presentation.id,
          role: OpenAIConstants.AI_ROLE_USER,
          content: suggestion,
        })
        this.generateSlidesAction({
          mode: 'Examples',
          example: true,
        })
      } catch (error) {
        Sentry.captureException(error)
      }
    },
    clickOnAddSlideButton(response, type) {
      const payload = {
        response,
        type,
        // passing mixins' functions as params
        updateCanvasBlocksFunction: this.updateCanvasBlocks,
        addActionToHistoryFunction: this.addActionToHistory,
      }
      this.addSlideToPresentationAction(payload)
    },
    clickOnFeedbackButton(feedback) {
      this.generateSlidesFromFeedbackAction(feedback)
    },
    async showGreetingsAndSuggestion() {
      try {
        await this.setGreetingsMessageAction()
        const hasPromptSuggestions = this.promptSuggestions && this.promptSuggestions.length
        if (hasPromptSuggestions) return
        this.getPromptSuggestionsAction()
      } catch (error) {
        Sentry.captureException(error)
      }
    },
    clickOnStopGeneratingSlidesButton() {
      this.stopGeneratingSlidesAction()
    },

    clickOnGenerateSlidesButton(prompt, mode) {
      if (!prompt || !prompt.length) return
      this.resetNumberOfSlidesAddedAction()
      this.setUserPromptAction(prompt)
      this.createNewMessageAction({
        presentationId: this.presentation.id,
        role: OpenAIConstants.AI_ROLE_USER,
        content: prompt,
      })
      this.generateSlidesAction({
        mode,
      })
    },

    enterAIPromptInput(prompt, mode) {
      this.clickOnGenerateSlidesButton(prompt, mode)
    },

    _trackOpenModalDocToQuiz() {
      const eventAction = 'AI > Attachment icon > Open import modal'
      const trackData = {
        eventAction,
        eventCategory: eventCategories.PRESENTATION_EDITOR,
        presentationId: this.presentation.id,
        userId: this.currentUser.id,
      }
      utils.trackGA4AndMixpanel(eventAction, trackData)
    },

    clickOnAIPromptInputAttachmentButton() {
      if (this.isDisableEditPresentation || this.isDocToSlideGenerating) return
      this.toggleDocToSlideImportModalAction(true)
      this._trackOpenModalDocToQuiz()
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";

.ai-controller {
  @apply w-full pb-4;
  @apply flex flex-col;
  @apply absolute top-0;
  height: calc(100% - $right-panel-header-height);
  margin-top: $right-panel-header-height;

  &.ai-controller--gpt-enabled {
    @apply pb-3;
  }
}

.overlay-disable-edit {
  @apply bg-base-0 opacity-50 absolute left-0 top-0 w-full h-full;
  z-index: 9;
}
</style>
